import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";
import Cookies from "js-cookie";
import {
  VisibilityIcon,
  VisibilityOffIcon,
  WarningIcon
} from "@echo-health/icons-web";
import styled from "../../styled";
import { isClient } from "../../global";
import { validateEmail } from "../../utils/validators";
import { useAuthenticateMutation } from "../../graphql/hooks/useAuthenticateMutation";
import { dropExistingUserCookie } from "../../marketing";
import { DispatchProp, State } from "../../store/types";
import { decode, encode } from "../../utils/queryString";
import AnonymousOnly from "../../components/AnonymousOnly";
import { ButtonGroup } from "../../components/DeprecatedButton";
import Form from "../../components/Form";
import { Heading } from "../../design-system/components/Heading";
import Icon from "../../components/Icon";
import { Text } from "../../design-system/components/Text";
import { DisplayHeading } from "../../design-system/components/DisplayHeading";
import { TextInput } from "../../design-system/components/TextInput";
import { TextLink } from "../../design-system/components/TextLink";
import { Button } from "../../design-system/components/Button";
import { getNHSLoginAuthInfo, NhsLoginInfo } from "../../utils/nhsLogin";
import { authenticateWithIntercom } from "../../login";
import { StateProps } from "./Root";
import { NHSLoginButton } from "../../components/NHSLoginButton";
import Divider from "../../components/Divider";
import { rebootIntercom } from "../../intercom";
import { StandaloneLayout } from "../../components/StandaloneLayout";
import { Card } from "../../design-system/components/Card";
import { LinkButton } from "../../design-system/components/LinkButton";
import useErrorModal, { getErrorVariant } from "../../components/ErrorModal";
import { Modal } from "../../design-system/components/Modal";
import {
  deprecatedTrack,
  reset as analyticsReset,
  trackEvent
} from "../../analytics";
import { useCreateOneTimeLoginTokenMutation } from "../../graphql/hooks/useCreateOneTimeLoginTokenMutation";
import config from "../../config";
import useIsHydrated from "../../hooks/useIsHydrated";
import withMargin, { WithMarginProp } from "../../styled/withMargin";
import { Illustration } from "../../design-system/components/Illustration";
import { TextButton } from "../../design-system/components/TextButton";
import { visuallyHidden } from "../../design-system/components/VisuallyHidden";
import { useToaster } from "../../design-system/components/Toaster";

interface PasswordInputWrapperProps extends WithMarginProp {
  $isVisible: boolean;
}

const PasswordInputWrapper = styled.div<PasswordInputWrapperProps>`
  ${p => !p.$isVisible && visuallyHidden}
  ${withMargin};
`;

const MagicLinkExplainer = styled.div`
  background-color: ${p => p.theme.color.gray10};
  padding: 12px;
  border-radius: ${p => p.theme.misc.borderRadius};
`;

const ForgotPasswordWrapper = styled.div`
  padding: ${p => `${p.theme.spacing.s} 12px 0`};
`;

const WrappingEmail = styled(Text)`
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
`;

type Props = RouteComponentProps & DispatchProp & StateProps;

const Login: React.FC<Props> = props => {
  const isHydrated = useIsHydrated();
  const { handleError } = useErrorModal();
  const { notify } = useToaster();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);
  const [hasLoginError, setHasLoginError] = useState(false);
  const [hasNoPatientError, setHasNoPatientError] = useState(false);
  const [nhsLoginInfo, setNHSLoginInfo] = useState<NhsLoginInfo | null>(null);

  // Hide the password input by default if token auth is available
  const [isPasswordInputVisible, setIsPasswordInputVisible] = useState(
    !(config.TOKEN_LOGIN_ENABLED === "true")
  );
  const [oneTimeLoginSuccess, setOneTimeLoginSuccess] = useState(false);

  const [authenticate, { loading: isAuthenticating }] = useAuthenticateMutation(
    {
      onError: error => {
        deprecatedTrack("login_failure");
        handleError({ variant: getErrorVariant(error) });
      },
      onCompleted: response => {
        const hasAuthErrors = response.authenticate.errors.length > 0;
        if (hasAuthErrors) {
          deprecatedTrack("login_failure_not_valid");
          setHasLoginError(true);
          return;
        }

        if (response.authenticate.account !== null) {
          const account = response.authenticate.account;
          authenticateWithIntercom({
            accountId: account.id,
            firstName: account.firstName,
            lastName: account.lastName,
            email: account.email,
            intercomId: account.intercomId,
            dispatch: props.dispatch,
            intercomUserHash: account.intercomUserHash
          });
        }

        dropExistingUserCookie();
        deprecatedTrack("login_successful");
        const query = decode(props.location.search);
        props.history.replace(query.returnUrl ? query.returnUrl[0] : "/");
      }
    }
  );

  const [
    createOneTimeLoginToken,
    { loading: isCreatingOneTimeLoginToken }
  ] = useCreateOneTimeLoginTokenMutation({
    onError: error => {
      deprecatedTrack("login_failure");
      handleError({ variant: getErrorVariant(error) });
    },
    onCompleted: () => {
      setOneTimeLoginSuccess(true);
      notify("Login email sent", {
        variant: "success"
      });
    }
  });

  useEffect(() => {
    const query = decode(props.location.search);

    if (query.sessionInvalid && query.sessionInvalid[0] === "true") {
      analyticsReset();
      rebootIntercom();
      const query = decode(props.location.search);
      delete query.sessionInvalid;
      const newQuery = encode(query);
      props.history.replace(props.location.pathname + newQuery);
      return;
    }

    // Don't use usePageView hook as we want to run this on the same render as if we reset analytics
    trackEvent("login_pageview");
  }, [props.location.search, props.location.pathname, props.history]);

  useEffect(() => {
    const nhsLoginInfo = getNHSLoginAuthInfo();
    setNHSLoginInfo(nhsLoginInfo);
    if (isClient()) {
      Cookies.set("nhs_login_auth_state", nhsLoginInfo.state, {
        domain: config.COOKIE_DOMAIN,
        secure: true
      });
      Cookies.set("nhs_login_nonce", nhsLoginInfo.nonce, {
        domain: config.COOKIE_DOMAIN,
        secure: true
      });
    }
  }, []);

  useEffect(() => {
    // Show the password input if it gets filled while hidden.
    // This usually happens when a password manager or browser
    // autofills the form. This would be faster for the patient to
    // log in than using the magic link.
    if (password.trim().length > 0 && !isPasswordInputVisible) {
      setIsPasswordInputVisible(true);
    }
  }, [password, isPasswordInputVisible]);

  const isNhsLoginEnabled =
    props.flags &&
    props.flags.length > 0 &&
    props.flags.find(flag => flag.name === "nhs_login_login" && flag.isEnabled);

  function onSubmit() {
    const validatedEmail = validateEmail(email);

    // Always check for a valid email
    if (validatedEmail) {
      setEmailError(validatedEmail);
      return;
    }

    // If we're emailing them a link to log in
    if (config.TOKEN_LOGIN_ENABLED === "true" && !isPasswordInputVisible) {
      // Create the token etc.
      createOneTimeLoginToken({ variables: { email } });
    } else {
      // Othewise validate the password and authenticate them
      if (password.trim() === "") {
        setPasswordError("Please enter your password");
        return;
      }

      authenticate({
        variables: {
          email: email.trim(),
          password
        }
      });
    }
  }

  return (
    <AnonymousOnly>
      <Helmet>
        <title>Log in | LloydsDirect</title>
      </Helmet>

      <StandaloneLayout contentWidth="420">
        {oneTimeLoginSuccess ? (
          <Card>
            <Illustration src="small-positive" />
            <DisplayHeading size="xl" margin="m 0">
              Check your email
            </DisplayHeading>
            <Text margin="0 0 m">
              We’ve sent an email to{" "}
              <WrappingEmail isBold isInline>
                {email}
              </WrappingEmail>
              . It has a link that will let you log in to your LloydsDirect
              account.
            </Text>

            <TextButton
              label="Log in with your password instead"
              onClick={() => {
                // Reset the login page
                setOneTimeLoginSuccess(false);
                setIsPasswordInputVisible(true);
              }}
            />

            <Divider />

            <Text isBold margin="0 0 s">
              Not received the email?
            </Text>
            <Text margin="0 0 m">
              It might take a few minutes to arrive. If you still don’t see it,
              check your spam folder.
            </Text>
            <ButtonGroup reverse>
              <Button
                label="Resend email"
                variant="secondary"
                onClick={() =>
                  createOneTimeLoginToken({ variables: { email } })
                }
                isLoading={isCreatingOneTimeLoginToken}
                width="fill"
              />
            </ButtonGroup>
          </Card>
        ) : (
          <>
            <Card margin="0 0 m">
              <DisplayHeading margin="0 0 l">Log in</DisplayHeading>
              <Form onSubmit={onSubmit}>
                <TextInput
                  type="email"
                  label="Email address"
                  id="email"
                  name="email"
                  value={email}
                  onChange={e => {
                    setEmail(e.currentTarget.value);
                    setEmailError("");
                  }}
                  onBlur={() => {
                    setEmailError(validateEmail(email));
                  }}
                  errorMessage={emailError}
                  autoComplete="email"
                  margin="0 0 m"
                />

                <PasswordInputWrapper
                  $isVisible={isPasswordInputVisible}
                  margin="0 0 m"
                >
                  <TextInput
                    type={isPasswordRevealed ? "text" : "password"}
                    label="Password"
                    id="current-password"
                    name="password"
                    value={password}
                    onChange={e => {
                      setPassword(e.currentTarget.value);
                      setPasswordError("");
                    }}
                    onBlur={e => {
                      if (
                        e.currentTarget.value.trim() === "" &&
                        isPasswordInputVisible
                      ) {
                        setPasswordError("Please enter your password");
                      }
                    }}
                    errorMessage={passwordError}
                    iconTrailing={{
                      icon: isPasswordRevealed
                        ? VisibilityIcon
                        : VisibilityOffIcon,
                      type: "button",
                      onClick: () => {
                        setIsPasswordRevealed(!isPasswordRevealed);
                      },
                      "aria-label": isPasswordRevealed
                        ? "Hide password"
                        : "Show password",
                      tabIndex: !isPasswordInputVisible ? -1 : undefined,
                      "aria-hidden": !isPasswordInputVisible
                    }}
                    tabIndex={!isPasswordInputVisible ? -1 : undefined}
                    aria-hidden={!isPasswordInputVisible}
                    autoComplete="current-password"
                  />
                  <ForgotPasswordWrapper>
                    <TextLink
                      label="Forgot your password?"
                      linkTo="/forgot-password"
                      type="internal"
                      size="s"
                      tabIndex={!isPasswordInputVisible ? -1 : undefined}
                      aria-hidden={!isPasswordInputVisible}
                    />
                  </ForgotPasswordWrapper>
                </PasswordInputWrapper>

                {config.TOKEN_LOGIN_ENABLED === "true" &&
                !isPasswordInputVisible ? (
                  <>
                    <Button
                      label="Log in with email"
                      shouldSubmit
                      isLoading={isCreatingOneTimeLoginToken}
                      isDisabled={!isHydrated}
                      onClick={() => {
                        trackEvent("login", { auth_type: "token" });
                      }}
                      margin="0 0 m"
                    />
                    <MagicLinkExplainer>
                      <Text size="s" margin="0 0 xs" color="gray70">
                        We’ll email you with a link for password-free login.
                      </Text>
                      <TextButton
                        label="Use your password instead"
                        onClick={() => setIsPasswordInputVisible(true)}
                        size="s"
                      />
                    </MagicLinkExplainer>
                  </>
                ) : (
                  <Button
                    label="Log in"
                    isLoading={isAuthenticating}
                    isDisabled={!isHydrated}
                    shouldSubmit
                    onClick={() => {
                      trackEvent("login", { auth_type: "email" });
                    }}
                  />
                )}
              </Form>

              {isNhsLoginEnabled && (
                <>
                  <Divider />
                  <NHSLoginButton
                    type="login"
                    href={nhsLoginInfo ? nhsLoginInfo.authUrl : ""}
                  />
                </>
              )}
            </Card>

            <Card>
              <Text isBold margin="0 0 s">
                Don’t have an account?
              </Text>
              <Text margin="0 0 m">
                Join the half a million people who already trust us with their
                NHS prescriptions
              </Text>
              <LinkButton
                label="Sign up for free"
                linkTo="/v2/sign-up"
                variant="secondary"
              />
            </Card>
          </>
        )}
      </StandaloneLayout>

      <Modal
        title="Incorrect email or password"
        isTitleVisible={false}
        isOpen={hasLoginError}
        onClose={() => {
          setHasLoginError(false);
        }}
      >
        <Icon margin="0 0 m" size="l" color="orange40" type={WarningIcon} />
        <Heading margin="0 0 s">Incorrect email or password</Heading>
        <Text>Unfortunately those login details aren't right.</Text>
        <Divider margin="l 0" />
        <ButtonGroup reverse>
          <Button
            label="Try again"
            onClick={() => {
              setHasLoginError(false);
              deprecatedTrack("incorrect_login_try_again");
            }}
          />
        </ButtonGroup>
      </Modal>

      <Modal
        title="Something's not right"
        isTitleVisible={false}
        isOpen={hasNoPatientError}
        onClose={() => {
          setHasNoPatientError(false);
        }}
      >
        <Icon margin="0 0 m" size="l" color="orange40" type={WarningIcon} />
        <Heading margin="0 0 s">Something's not right</Heading>
        <Text>
          It appears that you haven’t completed the sign up process. Before you
          can start using LloydsDirect you'll need to complete this.
        </Text>
        <Divider margin="l 0" />
        <ButtonGroup reverse>
          <LinkButton
            label="Complete your account"
            linkTo="/v2/sign-up"
            onClick={() => {
              setHasNoPatientError(false);
            }}
          />
        </ButtonGroup>
      </Modal>
    </AnonymousOnly>
  );
};

function mapStateToProps(state: State): StateProps {
  return {
    authenticated: state.auth.authenticated,
    flags: state.features.flags
  };
}

export default connect(mapStateToProps)(withRouter(Login));
